import { PRODUCT_PERMALINK_ROUTER } from '@/utils/static-router-alias'

export default {
  /**
   * https://irian.to/blogs/load-external-script-nuxt/
   * https://vueschool.io/articles/vuejs-tutorials/how-to-load-third-party-scripts-in-nuxt-js/
   * */
  head() {
    let thumb = this.exCover
    const width = 1920
    const height = 1080

    try {
      thumb = this.exCover.url.replace(
        process.env.DO_SPACES_CDN_ENDPOINT,
        `${process.env.DO_SPACES_CDN_ENDPOINT}/cdn-cgi/image/w=1920,h=1080,q=85,f=auto`
      )
    } catch (error) {}

    const images = []
    this.exGallery.forEach((photo) => images.push(photo.url))

    const countries =
      this.exExperience.starting_point_country !==
      this.exExperience.ending_point_country
        ? `${this.exExperience.starting_point_country}, ${this.exExperience.ending_point_country}`
        : `${this.exExperience.starting_point_country}`

    let title = this.$t('experience.meta.title', {
      name: this.exExperience.name,
      countries,
    })
    title = title.replace('Tour Tour ', 'Tour ')

    let description = ''
    if (
      this.exExperience.duration === 0 ||
      this.continent === null ||
      this.layawayAmount === 0
    ) {
      description = this.$t('experience.meta.description_legacy', {
        name: title,
      })
    } else {
      description = this.$t('experience.meta.description', {
        name: title,
        days: this.exExperience.duration,
        countries,
        continent: this.continent,
        price: this.layawayAmount,
        currency: this.currency,
      })
    }

    const json = {
      '@context': 'http://schema.org/',
      '@type': 'Product',
      name: title,
      image: images,
      description,
      mpn: this.exExperience.permalink.default,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: 3,
        reviewCount: 1,
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: this.currency,
        price: this.totalAmount.toFixed(2),
      },
    }

    if (this.exReviews.count > 0) {
      json.aggregateRating.ratingValue = this.exReviews.value.toString()
      json.aggregateRating.reviewCount = this.exReviews.count.toString()
    }

    const language = process.env.APP_LOCALE.toLowerCase().split('_')[0]
    /** se define por default experience xq en las landing-pages nunca hace match con el value */
    let baseType = 'experience'
    for (const [key, value] of Object.entries(
      PRODUCT_PERMALINK_ROUTER.alias[language]
    )) {
      if (value === this.$route.params.type) {
        baseType = key
      }
    }

    const permalinks = this.$store.state.experience.experience.permalink
    const canonical = `${process.env.APP_URL}${this.$route.path}`.replace(
      /\/landing\/|\/l\/|\/h\/|\/f\//g,
      `/${PRODUCT_PERMALINK_ROUTER.alias.en[baseType]}/`
    )

    const script = []

    if (this.totalAmount > 0) {
      script.push({
        hid: 'application-ld+json-details',
        type: 'application/ld+json',
        innerHTML: JSON.stringify(json),
      })
    }

    if ((this.exFaqs || []).length) {
      const mainEntity = this.exFaqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer,
        },
      }))

      script.push({
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity,
        },
      })
    }

    if ((this.breadcrumb || []).length) {
      const itemListElement = this.breadcrumb.map((_b, i) => {
        let item = `${process.env.APP_URL}${this.$route.path}`

        const { text, to } = _b

        if (to) {
          const {
            params: { type, value, tab },
          } = to

          item = `${process.env.APP_URL}/${type}/${value}/${tab}`
        }

        return {
          '@type': 'ListItem',
          position: i + 1,
          name: text,
          item,
        }
      })

      script.push({
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement,
        },
      })
    }

    const metadata = {
      title,
      meta: [
        /** Search Engine */
        {
          hid: 'description',
          name: 'description',
          content: description,
        },
        {
          hid: 'image',
          name: 'image',
          content: thumb,
        },

        /** Schema.org for Google */
        {
          hid: 'sg:name',
          itemprop: 'name',
          content: title,
        },
        {
          hid: 'sg:description',
          itemprop: 'description',
          content: description,
        },
        {
          hid: 'sg:image',
          itemprop: 'image',
          content: thumb,
        },

        /* Open Graph metatags */
        {
          hid: 'og:title',
          property: 'og:title',
          content: title,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: description,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: thumb,
        },
        {
          hid: 'og:image:secure_url',
          property: 'og:image:secure_url',
          content: thumb,
        },
        {
          hid: 'og:image:width',
          property: 'og:image:width',
          content: width,
        },
        {
          hid: 'og:image:height',
          property: 'og:image:height',
          content: height,
        },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `${process.env.APP_URL}${this.$route.path}`,
        },
        /* Twitter metatags */
        {
          hid: 'twitter:title',
          name: 'twitter:title',
          content: title,
        },
        {
          hid: 'twitter:description',
          name: 'twitter:description',
          content: description,
        },
        {
          hid: 'twitter:image',
          name: 'twitter:image',
          content: thumb,
        },
        {
          hid: 'twitter:image:width',
          name: 'twitter:image:width',
          content: width,
        },
        {
          hid: 'twitter:image:height',
          name: 'twitter:image:height',
          content: height,
        },
      ],
      /* canonical links */
      link: [
        {
          hid: 'canonical',
          rel: 'canonical',
          href: canonical,
        },
        {
          hid: 'alternate-default',
          rel: 'alternate',
          hreflang: 'x-default',
          href: `${process.env.APP_URL_EN_US}/${PRODUCT_PERMALINK_ROUTER.alias.en[baseType]}/${permalinks.en}`,
        },
        {
          hid: 'alternate-en',
          rel: 'alternate',
          hreflang: 'en',
          href: `${process.env.APP_URL_EN_US}/${PRODUCT_PERMALINK_ROUTER.alias.en[baseType]}/${permalinks.en}`,
        },
        {
          hid: 'alternate-es',
          rel: 'alternate',
          hreflang: 'es',
          href: `${process.env.APP_URL_ES_MX}/${PRODUCT_PERMALINK_ROUTER.alias.es[baseType]}/${permalinks.es}`,
        },
        {
          hid: 'alternate-es-CO',
          rel: 'alternate',
          hreflang: 'es-CO',
          href: `${process.env.APP_URL_ES_CO}/${PRODUCT_PERMALINK_ROUTER.alias.es[baseType]}/${permalinks.es}`,
        },
      ],
      script,
      __dangerouslyDisableSanitizers: ['innerHTML'],
    }

    if (this.exExperience.keywords) {
      metadata.meta.push({
        hid: 'keywords',
        name: 'keywords',
        content: this.exExperience.keywords,
      })
    }

    return metadata
  },
}
